import { createSignal, For, onCleanup, Show } from 'solid-js';
import PlayerHeadAvatar from './PlayerHeadAvatar';
import { RelativeTime } from './RelativeTime';
import { useApi, isOk } from '../api';
import { useTranslation } from '../hooks/useTranslation';
import type { KnownPlayerDto } from '../api';
import styles from './PlayerSearchPicker.module.css';

interface PlayerNameInputProps {
    value: string;
    onInput: (name: string) => void;
    placeholder?: string;
    ariaLabel?: string;
}

// Matches the backend's minimum; shorter prefixes match too many names to be worth listing.
const MIN_SEARCH_LENGTH = 3;

// A free-text Minecraft name field that suggests every player a bot has ever seen. Same dropdown as
// PlayerSearchPicker, which instead adds a picked profile to a player list and clears itself.
export default function PlayerNameInput(props: PlayerNameInputProps) {
    const api = useApi();
    const { t } = useTranslation();
    const [suggestions, setSuggestions] = createSignal<KnownPlayerDto[]>([]);
    const [focused, setFocused] = createSignal(false);
    const [activeIndex, setActiveIndex] = createSignal(-1);
    let debounceHandle: ReturnType<typeof setTimeout> | undefined;
    let latestRequestId = 0;

    const search = async (query: string) => {
        const requestId = ++latestRequestId;
        const res = await api.onlinePlayers.searchKnown({ q: query });
        if (requestId !== latestRequestId) return;
        setSuggestions(isOk(res.data) ? res.data.value : []);
        setActiveIndex(-1);
    };

    const onInput = (name: string) => {
        props.onInput(name);
        if (debounceHandle) clearTimeout(debounceHandle);
        if (name.trim().length < MIN_SEARCH_LENGTH) {
            latestRequestId++;
            setSuggestions([]);
            return;
        }
        debounceHandle = setTimeout(() => void search(name.trim()), 200);
    };

    onCleanup(() => { if (debounceHandle) clearTimeout(debounceHandle); });

    const pick = (player: KnownPlayerDto) => {
        props.onInput(player.name);
        setSuggestions([]);
    };

    const exactMatchOnly = () => suggestions().length === 1 && suggestions()[0].name === props.value.trim();
    const open = () => focused() && suggestions().length > 0 && !exactMatchOnly();

    const onKeyDown = (e: KeyboardEvent) => {
        if (!open()) return;
        const count = suggestions().length;
        if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIndex((activeIndex() + 1) % count); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIndex((activeIndex() - 1 + count) % count); }
        else if (e.key === 'Enter' && activeIndex() >= 0) { e.preventDefault(); pick(suggestions()[activeIndex()]); }
        else if (e.key === 'Escape') { e.preventDefault(); setSuggestions([]); }
    };

    return (
        <div class={styles.picker}>
            <input
                type="text"
                maxLength={16}
                autocomplete="off"
                spellcheck={false}
                role="combobox"
                aria-expanded={open()}
                aria-autocomplete="list"
                aria-label={props.ariaLabel}
                placeholder={props.placeholder}
                value={props.value}
                onInput={(e) => onInput(e.currentTarget.value)}
                onKeyDown={onKeyDown}
                onFocus={() => setFocused(true)}
                onBlur={() => setTimeout(() => setFocused(false), 120)}
            />
            <Show when={open()}>
                <div class={styles.dropdown} role="listbox">
                    <For each={suggestions()}>
                        {(player, index) => (
                            <button
                                type="button"
                                role="option"
                                aria-selected={index() === activeIndex()}
                                class={`${styles.option} ${index() === activeIndex() ? styles.optionActive : ''}`}
                                onMouseDown={(e) => { e.preventDefault(); pick(player); }}
                            >
                                <PlayerHeadAvatar name={player.name} size={24} showStatus online={player.currentlyOnline} />
                                <span class={styles.optionName}>{player.name}</span>
                                <span class={styles.optionUuid}>
                                    {player.currentlyOnline ? t('PLAYER_NAME_ONLINE_NOW') : <RelativeTime value={player.lastSeen} />}
                                </span>
                            </button>
                        )}
                    </For>
                </div>
            </Show>
        </div>
    );
}
